import { jsPDF } from "jspdf";

export type InvoicePdfItem = {
	name: string;
	qty: number;
	unit: number;
	line: number;
};

export type InvoicePdfData = {
	title: string;
	code?: string;
	id: string;
	status?: string;
	createdAt?: string;
	buyerName: string;
	buyerEmail?: string;
	buyerPhone?: string;
	buyerAddress?: string;
	description?: string;
	items: InvoicePdfItem[];
	deliveryCost?: number | null;
	total: number;
	bankName?: string;
	accountName?: string;
	accountNumber?: string;
	amountToPay?: number;
	paymentRef?: string;
};

const PAGE_WIDTH = 210;
const PAGE_HEIGHT = 297;
const MARGIN = 16;
const CONTENT_WIDTH = PAGE_WIDTH - MARGIN * 2;
const HEADER_H = 22;
const NAVY_TOP: [number, number, number] = [63, 46, 200];
const NAVY_BOTTOM: [number, number, number] = [31, 23, 98];
const PURPLE: [number, number, number] = [63, 46, 200];
const SLATE: [number, number, number] = [71, 85, 105];
const LINE: [number, number, number] = [226, 232, 240];

const LOGO_SRC = "/logo.png";
const LOGO_ASPECT = 358 / 76;

let cachedLogoDataUrl: string | null = null;

function money(value: number) {
	return `NGN ${value.toLocaleString("en-NG", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function fillVerticalGradient(
	doc: jsPDF,
	x: number,
	y: number,
	w: number,
	h: number,
	from: [number, number, number],
	to: [number, number, number]
) {
	const steps = 28;
	const stepH = h / steps;
	for (let i = 0; i < steps; i++) {
		const t = i / Math.max(steps - 1, 1);
		doc.setFillColor(
			Math.round(from[0] + (to[0] - from[0]) * t),
			Math.round(from[1] + (to[1] - from[1]) * t),
			Math.round(from[2] + (to[2] - from[2]) * t)
		);
		doc.rect(x, y + i * stepH, w, stepH + 0.15, "F");
	}
}

async function loadLogoDataUrl() {
	if (cachedLogoDataUrl)
		return cachedLogoDataUrl;
	try {
		const response = await fetch(LOGO_SRC);
		if (!response.ok)
			return null;
		const blob = await response.blob();
		cachedLogoDataUrl = await new Promise<string>((resolve, reject) => {
			const reader = new FileReader();
			reader.onload = () => resolve(String(reader.result));
			reader.onerror = () => reject(new Error("Failed to read logo"));
			reader.readAsDataURL(blob);
		});
		return cachedLogoDataUrl;
	}
	catch {
		return null;
	}
}

function drawHeader(doc: jsPDF, data: InvoicePdfData, logoDataUrl: string | null, compact = false) {
	const height = compact ? 16 : HEADER_H;
	fillVerticalGradient(doc, 0, 0, PAGE_WIDTH, height, NAVY_TOP, NAVY_BOTTOM);

	const logoH = compact ? 7 : 9;
	const logoW = logoH * LOGO_ASPECT;
	if (logoDataUrl) {
		doc.addImage(logoDataUrl, "PNG", MARGIN, (height - logoH) / 2, logoW, logoH);
	}
	else {
		doc.setTextColor(255, 255, 255);
		doc.setFont("helvetica", "bold");
		doc.setFontSize(compact ? 11 : 13);
		doc.text("INTASIDA", MARGIN, height / 2 + 1.5);
	}

	doc.setTextColor(255, 255, 255);
	doc.setFont("helvetica", "normal");
	doc.setFontSize(8);
	doc.text("INVOICE", PAGE_WIDTH - MARGIN, compact ? 6.5 : 8, { align: "right" });
	doc.setFont("helvetica", "bold");
	doc.setFontSize(10);
	doc.text(data.code || data.id, PAGE_WIDTH - MARGIN, compact ? 11.5 : 15, { align: "right" });
}

export async function buildInvoicePdf(data: InvoicePdfData) {
	const doc = new jsPDF({ unit: "mm", format: "a4" });
	const logoDataUrl = await loadLogoDataUrl();
	drawHeader(doc, data, logoDataUrl);

	const nextPage = (y: number, needed = 16) => {
		if (y + needed <= PAGE_HEIGHT - MARGIN)
			return y;
		doc.addPage();
		drawHeader(doc, data, logoDataUrl, true);
		return 24;
	};

	let y = HEADER_H + 12;
	doc.setTextColor(15, 23, 42);
	doc.setFont("helvetica", "bold");
	doc.setFontSize(14);
	const titleLines = doc.splitTextToSize(data.title || "Invoice", CONTENT_WIDTH);
	doc.text(titleLines, MARGIN, y);
	y += titleLines.length * 6 + 2;

	doc.setFont("helvetica", "normal");
	doc.setFontSize(9);
	doc.setTextColor(...SLATE);
	const meta = [
		data.status ? `Status: ${data.status}` : null,
		data.createdAt
			? `Date: ${new Date(data.createdAt).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}`
			: null
	].filter(Boolean) as string[];
	if (meta.length) {
		doc.text(meta.join("   ·   "), MARGIN, y);
		y += 8;
	}

	y = nextPage(y, 28);
	doc.setDrawColor(...LINE);
	doc.line(MARGIN, y, PAGE_WIDTH - MARGIN, y);
	y += 8;

	doc.setFont("helvetica", "bold");
	doc.setFontSize(8);
	doc.setTextColor(...PURPLE);
	doc.text("BILL TO", MARGIN, y);
	y += 5;
	doc.setTextColor(15, 23, 42);
	doc.setFont("helvetica", "bold");
	doc.setFontSize(11);
	doc.text(data.buyerName || "-", MARGIN, y);
	y += 5;
	doc.setFont("helvetica", "normal");
	doc.setFontSize(9);
	doc.setTextColor(...SLATE);
	for (const line of [data.buyerEmail, data.buyerPhone, data.buyerAddress].filter(Boolean) as string[]) {
		const wrapped = doc.splitTextToSize(line, CONTENT_WIDTH);
		y = nextPage(y, wrapped.length * 4.5);
		doc.text(wrapped, MARGIN, y);
		y += wrapped.length * 4.5;
	}

	if (data.description) {
		y += 3;
		y = nextPage(y, 10);
		doc.setTextColor(...SLATE);
		const desc = doc.splitTextToSize(data.description, CONTENT_WIDTH);
		doc.text(desc, MARGIN, y);
		y += desc.length * 4.5;
	}

	y += 6;
	y = nextPage(y, 18);
	doc.setFillColor(248, 250, 252);
	doc.rect(MARGIN, y, CONTENT_WIDTH, 8, "F");
	doc.setFont("helvetica", "bold");
	doc.setFontSize(8);
	doc.setTextColor(...SLATE);
	doc.text("ITEM", MARGIN + 2, y + 5.5);
	doc.text("QTY", MARGIN + 108, y + 5.5);
	doc.text("UNIT", MARGIN + 128, y + 5.5);
	doc.text("AMOUNT", PAGE_WIDTH - MARGIN - 2, y + 5.5, { align: "right" });
	y += 12;

	doc.setFont("helvetica", "normal");
	doc.setFontSize(9);
	if (!data.items.length) {
		doc.setTextColor(...SLATE);
		doc.text("No items on this invoice.", MARGIN + 2, y);
		y += 8;
	}
	else {
		for (const item of data.items) {
			const nameLines = doc.splitTextToSize(item.name, 100);
			const rowHeight = Math.max(nameLines.length * 4.5, 6) + 4;
			y = nextPage(y, rowHeight);
			doc.setTextColor(15, 23, 42);
			doc.setFont("helvetica", "bold");
			doc.text(nameLines, MARGIN + 2, y);
			doc.setFont("helvetica", "normal");
			doc.text(String(item.qty || "-"), MARGIN + 108, y);
			doc.text(money(item.unit), MARGIN + 128, y);
			doc.text(money(item.line), PAGE_WIDTH - MARGIN - 2, y, { align: "right" });
			y += rowHeight - 2;
			doc.setDrawColor(...LINE);
			doc.line(MARGIN, y, PAGE_WIDTH - MARGIN, y);
			y += 6;
		}
	}

	y += 2;
	y = nextPage(y, 28);
	const totalsX = PAGE_WIDTH - MARGIN;
	const labelX = totalsX - 70;
	doc.setFont("helvetica", "normal");
	doc.setFontSize(9);
	doc.setTextColor(...SLATE);
	if (data.deliveryCost != null) {
		doc.text("Delivery", labelX, y);
		doc.setTextColor(15, 23, 42);
		doc.text(money(data.deliveryCost), totalsX, y, { align: "right" });
		y += 6;
	}
	doc.setFont("helvetica", "bold");
	doc.setFontSize(11);
	doc.setTextColor(15, 23, 42);
	doc.text("Total", labelX, y);
	doc.setTextColor(...PURPLE);
	doc.text(money(data.total), totalsX, y, { align: "right" });
	y += 12;

	if (data.accountNumber) {
		y = nextPage(y, 52);
		doc.setFillColor(245, 243, 255);
		const boxTop = y;
		const boxHeight = data.paymentRef ? 52 : 46;
		doc.roundedRect(MARGIN, boxTop, CONTENT_WIDTH, boxHeight, 3, 3, "F");
		doc.setFont("helvetica", "bold");
		doc.setFontSize(8);
		doc.setTextColor(...PURPLE);
		doc.text("BANK TRANSFER DETAILS", MARGIN + 6, boxTop + 8);
		doc.setFont("helvetica", "normal");
		doc.setFontSize(9);
		doc.setTextColor(15, 23, 42);
		const payLines = [
			`Bank: ${data.bankName || "SafeHaven Microfinance Bank"}`,
			`Account name: ${data.accountName || "Intasida"}`,
			`Account number: ${data.accountNumber}`,
			`Amount: ${money(data.amountToPay ?? data.total)}`,
			data.paymentRef ? `Reference: ${data.paymentRef}` : null
		].filter(Boolean) as string[];
		let py = boxTop + 15;
		for (const line of payLines) {
			doc.text(line, MARGIN + 6, py);
			py += 6;
		}
		y = boxTop + boxHeight + 8;
		doc.setFontSize(8);
		doc.setTextColor(...SLATE);
		doc.text("Transfer the exact amount. You can copy the account number and reference from this PDF.", MARGIN, y);
	}

	doc.setFont("helvetica", "normal");
	doc.setFontSize(8);
	doc.setTextColor(148, 163, 184);
	doc.text("Generated by Intasida", PAGE_WIDTH / 2, PAGE_HEIGHT - 10, { align: "center" });

	return doc;
}

export function invoicePdfFilename(data: InvoicePdfData) {
	const slug = (data.code || data.id).replace(/[^\w.-]+/g, "-");
	return `invoice-${slug}.pdf`;
}

export async function shareOrDownloadInvoicePdf(data: InvoicePdfData) {
	const doc = await buildInvoicePdf(data);
	const blob = doc.output("blob");
	const filename = invoicePdfFilename(data);
	const file = new File([blob], filename, { type: "application/pdf" });

	let canShareFile = false;
	try {
		canShareFile
			= typeof navigator !== "undefined"
			&& typeof navigator.share === "function"
			&& typeof navigator.canShare === "function"
			&& navigator.canShare({ files: [file] });
	}
	catch {
		canShareFile = false;
	}

	if (canShareFile) {
		try {
			await navigator.share({
				files: [file],
				title: data.title || "Invoice",
				text: `Invoice ${data.code || data.id}`
			});
			return "shared" as const;
		}
		catch (error) {
			if ((error as DOMException)?.name === "AbortError")
				return "cancelled" as const;
		}
	}

	const url = URL.createObjectURL(blob);
	const link = document.createElement("a");
	link.href = url;
	link.download = filename;
	link.rel = "noopener";
	document.body.appendChild(link);
	link.click();
	link.remove();
	window.setTimeout(() => URL.revokeObjectURL(url), 1000);
	return "downloaded" as const;
}
